import { GridFilterItem, GridFilterInputValueProps } from '@mui/x-data-grid'
import { getGridDateOperators, GridFilterOperator } from '@mui/x-data-grid-premium'
import { format } from 'date-fns'
import React from 'react'

/**
 * Wraps MUI's date filter operators so that Date values produced by
 * GridFilterInputDate are converted to ISO-8601 date strings (YYYY-MM-DD)
 * before being stored in the filter model.
 *
 * This is necessary because MUI's date input component calls
 * `applyValue({ ...item, value: new Date(value) })`, but our Zod schema
 * (DateFilterItemSchema) expects `z.string().date()`.
 */
export const getDateFilterOperators = (): GridFilterOperator[] => {
  const operators = getGridDateOperators()

  // All date operators with an InputComponent use the same underlying component
  // (GridFilterInputDate). We must reuse a single wrapper reference so MUI's
  // operator-change logic doesn't erase the value (it compares InputComponent identity).
  const originalInputComponent = operators.find((op) => op.InputComponent)?.InputComponent
  const sharedWrappedComponent = originalInputComponent ? createWrappedDateInput(originalInputComponent) : undefined

  return operators.map((operator) => {
    if (!operator.InputComponent) {
      return operator
    }
    return { ...operator, InputComponent: sharedWrappedComponent }
  })
}

const createWrappedDateInput = (OriginalInputComponent: React.JSXElementConstructor<GridFilterInputValueProps>) => {
  const WrappedDateInput = (props: GridFilterInputValueProps) => {
    const wrappedApplyValue = (value: GridFilterItem) => {
      if (value.value instanceof Date) {
        props.applyValue({ ...value, value: format(value.value, 'yyyy-MM-dd') })
        return
      }
      props.applyValue(value)
    }
    return <OriginalInputComponent {...props} applyValue={wrappedApplyValue} />
  }
  WrappedDateInput.displayName = 'WrappedDateInput'
  return WrappedDateInput
}
